<template>
    <div>
        <div class="display-inline" v-on:click="noticeModal($event)">
            <slot name="modal-link"></slot>
        </div>
        <modal :show.sync="modals.classic" headerClasses="justify-content-center">
            <h4 slot="header" class="title title-up">Upload Documents</h4>
            <hr />
            <div class="container">
                <!-- Browse Button -->
                <div class="row">
                    <div class="col-md-7">
                        <label>Please select a document to upload:<br>
                            <small class="red">*PDF/PNG/JPG/JPEG Document files with a size less than 10MB</small>
                        </label>
                    </div>
                    <div
                        class="col-md-5 d-flex justify-content-end more-top-padding more-bottom-padding no-right-padding">
                        <base-button type="file">
                            <span><i class="tim-icons icon-laptop"></i> Browse</span>
                            <input accept=".pdf,.png,.jpg,.jpeg" :error="getError('file')" name="file" type="file"
                                @change="setUploadedFile" />
                        </base-button>
                    </div>
                </div>
                <!-- Selected File -->
                <div v-if="document.file" class="row">
                    <div class="col-md-3">
                        <label>Selected File</label>
                    </div>
                    <base-input v-model="document.file.target.files[0].name" class="col-md-9" type="text"
                        placeholder="No file selected" addon-left-icon="tim-icons icon-attach-87" disabled></base-input>
                </div>

                <base-input>
                    <label>Types </label>
                    <v-select v-model="document.type" name="type" label="name" :reduce="types => types.id"
                        :options="userFileTypes" placeholder="Select one"></v-select>
                </base-input>

                <base-input>
                    <label>Remark</label>
                    <textarea class="form-control" placeholder="(Optional) Add notes about this document..." rows="3"
                        v-model="document.remark"></textarea>
                </base-input>
            </div>
            <template slot="footer">
                <div class="container alert_footer">
                    <base-button type="danger" @click.native="modals.classic = false" class="mr-4">
                        <span><i class="tim-icons icon-simple-remove"></i> Cancel</span>
                    </base-button>
                    <base-button :disabled="!document.file" type="primary" @click.native="submit">
                        <span><i class="tim-icons icon-cloud-upload-94"></i> {{ savingDocument ? "Saving..." : "Upload"
                            }}</span>
                    </base-button>
                </div>
            </template>
        </modal>

    </div>
</template>

<script>
import {
    BaseButton,
    Modal
} from '@/components'
import {
    Upload
} from 'element-ui'
import LoadingAlertMixin from '@/mixins/docLoadingAlert';
import swal from "sweetalert2";
import { mapActions } from "vuex";
import { compressImage, isCompressibleImage } from '@/util/imageCompressor';

const options = {
    'Content-Type': 'application/json',
    config: { showToast: false }
};

export default {
    name: "UserDocumentsModal",
    mixins: [LoadingAlertMixin],
    props: {
        email: {
            type: String,
            default: null
        },
        type: {
            type: String,
            default: null
        },
        document: {
            type: Object,
            default: function () {
                return {
                    id: 0,
                    file: null,
                    type: null,
                    remark: ""
                }
            }
        },
        isIcon: {
            type: Boolean,
            default: false
        },
        isNew: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            modals: {
                classic: false,
            },
            userFileTypes: [],
            savingDocument: false,
        };
    },
    components: {
        BaseButton,
        Modal,
        Upload
    },
    computed: {
        fileRule() {
            return this.isNew ? 'required' : ''
        }
    },
    methods: {
        ...mapActions({
            getUser: "users/getUser"
        }),
        noticeModal: function (event) {
            this.openModal()
        },
        openModal() {
            let ctx = this;
            ctx.getUserFileTypes().then(() => {
                ctx.modals.classic = true
            })
        },
        async getUserFileTypes() {
            const response = await this.axios.get("/api/applicationuserfiletypes");
            this.userFileTypes = response.data.sort(function (a, b) {
                if (!a || !b) return 0;
                var textA = (a.name || "").toUpperCase();
                var textB = (b.name || "").toUpperCase();
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
            });
        },
        setUploadedFile(e) {
            var files = e.target.files
            var extension = files[0].type
            if (extension != "application/pdf" && extension != "image/png" && extension != "image/jpeg" && extension != "image/jpg") {
                this.$toast.error("Invalid File Format (Please upload PDF/PNG/JPG/JPEG Document files with a size less than 10M)");
                return
            } else {
                this.document.file = e
            }
        },
        submit(e) {
            if (this.savingDocument) return;
            let ctx = this;
            
            if(!ctx.document.type){
                ctx.$toast.error('Document Type is Required.');
                return
            }

            ctx.$validator.validateAll().then((result) => {
                if (result) {
                    this.savingDocument = true;
            this.showLoadingSweetAlert(
                "Your document is uploading.<br><br>This message will disappear automatically in 10 seconds."
            );
                    ctx.saveFile(ctx.$route.params.id)
                        .then(async response => {
                            ctx.savingDocument = false;
                            ctx.closeLoadingAlert();
                            ctx.modals.classic = false

                            ctx.document.file = null
                            ctx.document.type = null
                            
                            // Refresh user data is handled by the parent component via 'reloadDocuments' event
                            
                            ctx.$emit("reloadDocuments")
                            ctx.$toast.success('Document added successfully');
                        })
                        .catch((error) => {
                            ctx.savingDocument = false;
                            ctx.closeLoadingAlert();
                            
                            // Extract the error message from the response
                            let errorMessage = "Error uploading document";
                            if (error.response && error.response.data) {
                                if (typeof error.response.data === 'string') {
                                    errorMessage = error.response.data;
                                } else if (error.response.data.detail) {
                                    errorMessage = error.response.data.detail;
                                } else if (error.response.data.message) {
                                    errorMessage = error.response.data.message;
                                } else if (error.response.data.error) {
                                    errorMessage = error.response.data.error;
                                } else if (error.response.data.title) {
                                    errorMessage = error.response.data.title;
                                }
                            }
                            ctx.$toast.error(errorMessage);
                        });
                } else {
                    ctx.savingDocument = false;
                    ctx.closeLoadingAlert();
                }
            })
        },
        async saveFile() {
            let ctx = this;
            //const localStore = JSON.parse(localStorage.getItem("user"));
            let formData = new FormData();
            let fileToUpload = ctx.document.file.target.files[0];

            // Compress image files before upload to reduce payload size
            if (isCompressibleImage(fileToUpload)) {
              try {
                fileToUpload = await compressImage(fileToUpload);
              } catch (err) {
                console.warn('Image compression skipped:', err);
              }
            }

            formData.append('IFile', fileToUpload);
            // formData.append('Id', this.document.id)
            if (ctx.email) {
                formData.append('Email', ctx.email)
            }
            formData.append('Remark', ctx.document.remark)
            formData.append('FileTypeId', ctx.document.type)
            formData.append('DocumentDate', new Date().toISOString())

            // for user profile/regristrations we just want to allowe the urer end point
            // for everyone else pass through the admin validations
            // if no type is passed we also assum its personal
            let url = (!this.type || this.type === "personal")
                ? `api/applicationuserfiles/user/` : `api/applicationuserfiles/admin`;

            return ctx.axios.post(url, formData, options)



        },
        getError(fieldName) {
            return this.errors.first(fieldName);
        }
    }
}
</script>
